import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AsyncBoundary } from '@/components/AsyncBoundary/AsyncBoundary';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import { DataTable, type Column } from '@/components/DataTable/DataTable';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { TextField } from '@/components/Field/Field';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { CohortStateBadge } from '@/features/admin/components/CohortStateBadge/CohortStateBadge';
import { api } from '@/lib/api/endpoints';
import type { Cohort } from '@/lib/api/types';
import { formatDate } from '@/lib/format';
import { useAsync } from '@/lib/hooks/useAsync';
import { useSubmit } from '@/lib/hooks/useSubmit';
import styles from './pages.module.scss';

export function CohortsPage() {
  const { state, reload } = useAsync(() => api.cohorts.list().then((result) => result.cohorts), []);

  const columns: ReadonlyArray<Column<Cohort>> = [
    {
      key: 'name',
      header: 'Cohort',
      render: (cohort) => <Link to={`/admin/cohorts/${cohort.id}`}>{cohort.name}</Link>,
    },
    { key: 'state', header: 'State', render: (cohort) => <CohortStateBadge state={cohort.state} /> },
    { key: 'date', header: 'Workshop', render: (cohort) => formatDate(cohort.workshop_date) },
    {
      key: 'window',
      header: 'Access window',
      secondary: true,
      render: (cohort) =>
        `${formatDate(cohort.access_starts_at)} → ${formatDate(cohort.access_expires_at)}`,
    },
  ];

  return (
    <div className={styles.stack}>
      <PageHeader
        eyebrow="Epic 3"
        title="Cohorts"
        description="A cohort pins one workshop source version to one group of attendees for one time window. Draft cohorts are invisible to attendees."
      />

      <CreateCohortForm onCreated={reload} />

      <Card title="All cohorts">
        <AsyncBoundary state={state} subject="cohorts" onRetry={reload}>
          {(cohorts) => (
            <DataTable
              caption="Cohorts"
              columns={columns}
              rows={cohorts}
              rowKey={(cohort) => cohort.id}
              empty={
                <EmptyState
                  title="No cohorts yet"
                  description="Create a cohort against a registered source to start granting attendee access."
                />
              }
            />
          )}
        </AsyncBoundary>
      </Card>
    </div>
  );
}

interface CohortDraft {
  name: string;
  source_id: string;
  workshop_date: string;
  access_starts_at: string;
  access_expires_at: string;
}

const EMPTY_DRAFT: CohortDraft = {
  name: '',
  source_id: '',
  workshop_date: '',
  access_starts_at: '',
  access_expires_at: '',
};

/**
 * US-3.1. The date rules are re-checked here purely so the admin gets an
 * immediate answer; the same constraints are enforced by CHECK constraints on
 * the cohorts table, which is what actually holds.
 */
function validate(draft: CohortDraft): string | null {
  if (!draft.name.trim()) return 'A cohort name is required.';
  if (draft.access_expires_at <= draft.workshop_date)
    return 'Access must expire after the workshop date.';
  if (draft.access_expires_at <= draft.access_starts_at)
    return 'Access must expire after it starts.';
  return null;
}

function CreateCohortForm({ onCreated }: { onCreated: () => void }) {
  const [draft, setDraft] = useState<CohortDraft>(EMPTY_DRAFT);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { submit, busy, message } = useSubmit(api.cohorts.create, () => {
    setDraft(EMPTY_DRAFT);
    onCreated();
  });

  const set = (key: keyof CohortDraft) => (event: { target: { value: string } }) =>
    setDraft((current) => ({ ...current, [key]: event.target.value }));

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const problem = validate(draft);
    setValidationError(problem);
    if (!problem) void submit(draft);
  };

  return (
    <Card title="Create a cohort (US-3.1)">
      <form onSubmit={onSubmit}>
        <div className={styles.formGrid}>
          <TextField
            label="Cohort name"
            required
            placeholder="Autumn 2026 — Product Team"
            hint="Unique across the platform. Your casing and punctuation are preserved."
            value={draft.name}
            onChange={set('name')}
          />
          <TextField
            label="Source version"
            required
            placeholder="src_0193…"
            hint="The registered source this cohort is pinned to for the whole session (NFR-AP3)."
            value={draft.source_id}
            onChange={set('source_id')}
          />
          <TextField
            label="Workshop date"
            type="date"
            required
            value={draft.workshop_date}
            onChange={set('workshop_date')}
          />
          <TextField
            label="Access starts"
            type="date"
            required
            value={draft.access_starts_at}
            onChange={set('access_starts_at')}
          />
          <TextField
            label="Access expires"
            type="date"
            required
            error={validationError ?? undefined}
            hint="Access stops server-side at this point, even for an open tab (NFR-AP5)."
            value={draft.access_expires_at}
            onChange={set('access_expires_at')}
          />
        </div>
        <div className={styles.formActions}>
          {message && <span className={styles.note}>{message}</span>}
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create cohort'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
